import React, { useState } from 'react';
import {
  Button,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Image,
  Input,
  Layout,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  useApi,
} from '@axiom/ui';
import type { User } from '@axiom/validation';

import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';
import MoreProfiles from '../../public/images/more-profiles.png';
import { isAFCUser } from '../../utils/roles-util';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { BarredLocationsLegacyApi } from '../../api/barred-locations-legacy-api';
import { ExternalSearchReachOutModalSchema } from '../../schema/external-search-reach-out-modal-schema';
import { HubspotUtil } from '../../utils/hubspot-util';
import { TalentSearchFilterUtil } from '../../utils/talent-search-filter-util';
import { postExternalSearchReachOut } from '../../api/hubspot-api';

export interface ExternalSearchReachOutModalProps {
  activeFilters: TalentSearchActiveQueryFilters;
  user: User | null;
  onClose: () => void;
}

export const ExternalSearchReachOutModal = ({
  activeFilters,
  user,
  onClose,
}: ExternalSearchReachOutModalProps) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const [{ data: barredLocations }] = useApi(
    BarredLocationsLegacyApi.readBarredLocations()
  );
  const [showThankYou, setShowThankYou] = useState(false);
  const isAuthed = isAFCUser(user);

  return (
    <Form
      name="EXTERNAL_REACH_OUT_MODAL_FORM"
      schema={ExternalSearchReachOutModalSchema}
      initialValues={{
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        email: user?.email ?? '',
        phoneNumber: null,
        agreeToTermsOfService: true,
        createAccount: false,
      }}
      onSubmit={async (_, __, formData) => {
        const dataUTM = HubspotUtil.parseURLDataUtm();
        const utmArr = Object.entries(dataUTM).map(entry => ({
          name: entry[0],
          value: entry[1],
        }));
        const data = {
          fields: [
            { name: 'firstname', value: formData.firstName },
            { name: 'lastname', value: formData.lastName },
            { name: 'email', value: formData.email },
            {
              name: 'agree_to_terms_of_service',
              value: true,
            },
            { name: 'create_account', value: formData.createAccount || false },
            { name: 'filters', value: JSON.stringify(formData.activeFilters) },
            ...utmArr,
          ],
        };

        if (formData.phoneNumber) {
          data.fields.push({
            name: 'phone',
            value: formData.phoneNumber.replaceAll(/[ ()+-]/g, ''),
          });
        }

        await postExternalSearchReachOut(data);
        setShowThankYou(true);
      }}
    >
      {({ fireSubmit, values, setValues }) => {
        return showThankYou ? (
          <Modal size="confirm" name="EXTERNAL_REACH_OUT_CONFIRMATION_MODAL">
            <ModalHeader
              name="EXTERNAL_REACH_OUT_CONFIRMATION_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              We're on it
            </ModalHeader>
            <ModalSection>
              We'll get your request to the right person at Axiom. You can
              expect to hear back from us soon.
            </ModalSection>
            <ModalFooter>
              <Button
                name="got_it"
                onClick={() => {
                  onClose();
                }}
              >
                Got it!
              </Button>
            </ModalFooter>
          </Modal>
        ) : (
          <Modal name="EXTERNAL_REACH_OUT_MODAL">
            <ModalHeader
              name="EXTERNAL_REACH_OUT_MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              More profiles please
            </ModalHeader>
            <ModalSection>
              <Layout position="center">
                <Gutter bottom="16px">
                  <Image
                    alt="more-people"
                    name="MORE_PROFILES"
                    src={MoreProfiles}
                    width="100px"
                  />
                </Gutter>
              </Layout>
              <Gutter bottom="16px">
                <Paragraph>
                  Axiom can help find exactly what you're looking for. How can
                  we reach you?
                </Paragraph>
              </Gutter>
              <Grid stackable>
                <GridRow gutterBottom="16px">
                  <GridColumn width={6}>
                    <Input
                      label="First Name"
                      name="firstName"
                      disabled={isAuthed}
                    />
                  </GridColumn>
                  <GridColumn width={6}>
                    <Input
                      label="Last Name"
                      name="lastName"
                      disabled={isAuthed}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={6}>
                    <Input label="Email" name="email" disabled={isAuthed} />
                  </GridColumn>
                  <GridColumn width={6}>
                    <Input label="Phone Number" name="phoneNumber" />
                  </GridColumn>
                </GridRow>
                <Gutter bottom="8px" />
              </Grid>
            </ModalSection>
            <ModalFooter>
              <Button
                variation="outline"
                onClick={() => {
                  onClose();
                }}
                name="cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await setValues({
                    ...values,
                    activeFilters: TalentSearchFilterUtil.getFilterData({
                      barredLocations,
                      taxonomy,
                      activeFilters,
                    }),
                  });
                  await fireSubmit();
                }}
                name="submit"
              >
                {isAuthed ? 'Submit' : 'Agree and Submit'}
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
